import {
  Component,
  ViewChild,
  OnInit,
  ViewContainerRef,
  TemplateRef,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  ChangeDetectorRef,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyButton as MatButton } from '@angular/material/legacy-button';
import { TemplatePortal } from '@angular/cdk/portal';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';

import { Subject, Subscription } from 'rxjs';
import { debounceTime, take, distinctUntilChanged, takeUntil, switchMap, filter } from 'rxjs/operators';
import { AccountService } from 'app/core';
import { EMAIL_PATTERN } from 'app/shared/Utils/common.utils';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { ContactsService } from 'app/modules/contacts/contacts.service';
import { Contact, Category } from 'app/modules/contacts/contacts.types';
import { PanelsInterfaceComponent, PanelsInterfaceComponentBase } from '../panels-interface.component';
import { ContactSendMessageComponent } from 'app/modules/contacts/modals/send-message/send-message.component';
import { ProjectAddContactComponent } from 'app/modules/projects/modals/project-add-contact/project-add-contact.component';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { NewPermission, Permission } from '@core/enums/permission';

@Component({
  selector: 'project-panel-contacts',
  templateUrl: './contacts.component.html',
  styleUrls: ['./contacts.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PanelsContactsComponent extends PanelsInterfaceComponentBase implements PanelsInterfaceComponent, OnInit {
  @ViewChild('addContactPanel') private _addContactPanel: TemplateRef<any>;
  @ViewChild('addContactPanelOrigin') private _addContactPanelOrigin: MatButton;
  NewPermission = NewPermission;
  private _addContactPanelOverlayRef: OverlayRef;
  private _addContactPanelTemplatePortal: TemplatePortal;
  private _unsubscribeAll: Subject<void>;

  public results: any[] | null;
  public selectedContact: Contact;
  public categories: Category[];

  contacts: Contact[];
  contactsCount: number = 0;
  contactForm: UntypedFormGroup;

  constructor(
    private readonly _projectsService: ProjectsService,
    private readonly _contactsService: ContactsService,
    protected readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _overlay: Overlay,
    private readonly _matDialog: MatDialog,
    private readonly _viewContainerRef: ViewContainerRef,
    private readonly _formBuilder: UntypedFormBuilder,
    private readonly _accountService: AccountService,
    private readonly confirmService: ConfirmService,
  ) {
    super(_changeDetectorRef);
    this._unsubscribeAll = new Subject();
    this._accountService.currentAccount.pipe(takeUntil(this._unsubscribeAll)).subscribe((accountData) => {
      this._projectsService.accountId = accountData.id;
      this._contactsService.accountId = accountData.id;
    });
  }

  ngOnInit() {
    this.contactForm = this._formBuilder.group({
      id: [''],
      category_id: ['', [Validators.required]],
      name: ['', [Validators.required]],
      company_name: [''],
      category_name: [''],
      email: ['', [Validators.email, Validators.pattern(EMAIL_PATTERN)]],
      phone: [''],
    });
    this.titleFieldDef = {
      name: 'contacts.panel_name',
      label: this.panelDef.title,
      type: 'text',
    };

    this._projectsService
      .getContacts(this.project)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((contact) => {
        if (contact) {
          this.contacts = contact;
          this.contactsCount = this.contacts.length;
        }

        this._changeDetectorRef.markForCheck();
      });
    this._projectsService.contacts$.pipe(takeUntil(this._unsubscribeAll)).subscribe((contacts: Contact[]) => {
      if (contacts) {
        this.contacts = contacts;
        this.contactsCount = contacts.length;
      }
      this._changeDetectorRef.markForCheck();
    });

    this._accountService.getCategories();
    this._accountService.categories$.pipe(takeUntil(this._unsubscribeAll)).subscribe((categories: Category[]) => {
      if (categories) {
        this.categories = categories.filter((option) => !option.hidden);
      }
      this._changeDetectorRef.markForCheck();
    });

    this.contactForm
      .get('name')
      .valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(400), distinctUntilChanged())
      .subscribe((value) => {
        let query = value;
        if (!query.id) {
          this.contactForm.get('id').setValue('');
          this._contactsService
            .getContacts(1, 20, 'first_name', 'asc', query)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((response) => {
              this.results = response.contacts;
              this._changeDetectorRef.markForCheck();
            });
        }
      });

    this.panelColor = this.panelDef?.color;
  }

  openContactModal(): void {
    this._matDialog
      .open(ProjectAddContactComponent, {
        data: { byPass: false },
      })
      .afterClosed()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => this.toggleEditMode(false));
  }

  openContactSendMessageModal(contact: Contact): Subscription {
    const dialogRef = this._matDialog.open(ContactSendMessageComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: { contact: contact },
    });
    return dialogRef.afterClosed().pipe(takeUntil(this._unsubscribeAll)).subscribe();
  }

  openAddContactPanel(): void {
    this._addContactPanelOverlayRef = this._overlay.create({
      backdropClass: '',
      hasBackdrop: true,
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(this._addContactPanelOrigin._elementRef.nativeElement)
        .withFlexibleDimensions()
        .withViewportMargin(64)
        .withLockedPosition()
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
        ]),
    });

    // Subscribe to the attachments observable
    this._addContactPanelOverlayRef
      .attachments()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        // Focus to the search input once the overlay has been attached
        this._addContactPanelOverlayRef.overlayElement.querySelector('input').focus();
      });

    // Create a portal from the template
    this._addContactPanelTemplatePortal = new TemplatePortal(this._addContactPanel, this._viewContainerRef);

    // Attach the portal to the overlay
    this._addContactPanelOverlayRef.attach(this._addContactPanelTemplatePortal);

    // Subscribe to the backdrop click
    this._addContactPanelOverlayRef
      .backdropClick()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => this._closeAddContactPanel());
  }

  private _closeAddContactPanel(): void {
    // If template portal exists and attached...
    if (this._addContactPanelTemplatePortal && this._addContactPanelTemplatePortal.isAttached) {
      // Detach it
      this._addContactPanelTemplatePortal.detach();
    }

    // If overlay exists and attached...
    if (this._addContactPanelOverlayRef && this._addContactPanelOverlayRef.hasAttached()) {
      // Detach it
      this._addContactPanelOverlayRef.detach();
      this._addContactPanelOverlayRef.dispose();
    }
    this._changeDetectorRef.markForCheck();
  }

  addEmailField(): void {
    // Create an empty email form group
    const emailFormGroup = this._formBuilder.group({
      id: [''],
      name: [''],
      company_name: [''],
      category_name: [''],
      email: ['', [Validators.email, Validators.pattern(EMAIL_PATTERN)]],
      phone: [''],
    });
    this._changeDetectorRef.markForCheck();
  }

  saveContactAssociation(): void {
    let contact = this.contactForm.value;

    if (contact) {
      this._projectsService.project$.pipe(take(1), takeUntil(this._unsubscribeAll)).subscribe((project) => {
        if (contact.id) {
          this._projectsService
            .createContactAssociation(contact, project.id)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe();
        } else {
          this._projectsService
            .createContact(contact)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((newContact) => {
              this._projectsService
                .createContactAssociation(newContact, project.id)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe();
            });
        }
        this._closeAddContactPanel();
        this.toggleEditMode();
      });
    }
  }

  deleteAssociatedContact(contact: Contact): void {
    this.confirmService
      .openConfirmationModal({
        title: 'Disassociate Contact Confirmation',
        message: `Are you sure you want to disassociate the contact ${contact.name}?`,
      })
      .pipe(filter((res: boolean) => res))
      .subscribe(() => {
        this._projectsService.project$
          .pipe(
            take(1),
            takeUntil(this._unsubscribeAll),
            switchMap((project) => this._projectsService.deleteContactAssociation(+contact.id, project.id)),
          )
          .subscribe(() => this._changeDetectorRef.markForCheck());
      });
  }

  cancelEdits(): void {
    this.toggleEditMode(false);
  }

  autocompleteSelected($event): void {
    this.contactForm.patchValue($event.option.value, { emitEvent: false });
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  displayFn(contact: Contact): string {
    return contact && contact.name ? contact.name : '';
  }
}
